import { useState, useEffect, useMemo } from "react";
import { startActivityTracking, stopActivityTracking } from "../lib/dailyPlan";
import { useParams, Link } from "react-router-dom";
import { Check, X, RotateCcw, BookOpen, Zap, ArrowRight, Shuffle } from "lucide-react";
import lessons from "../data/lessons.json";
import { progressAPI } from "../api";
import { earnXP } from "../gamification";
import { playCorrect, playWrong, playLevelUp } from "../sounds";
import { generateTestQuestions } from "../lib/sentenceGenerator";

export default function Test() {
  const { day } = useParams();
  const lesson = lessons.find((l) => l.day === Number(day));
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [seed, setSeed] = useState(0); // bump to regenerate dynamic questions

  // Track test time toward daily 1hr written target
  useEffect(() => {
    startActivityTracking("written", "test");
    return () => stopActivityTracking();
  }, []);

  // Build questions: either dynamic (new lessons w/ generatorKey) or static (old lessons.test)
  const questions = useMemo(() => {
    if (!lesson) return [];
    if (lesson.generatorKey) {
      return generateTestQuestions(lesson.generatorKey, 5);
    }
    return lesson.test || [];
    // seed in deps so "🎲 New questions" regenerates
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lesson, seed]);

  if (!lesson) {
    return (
      <div className="text-center py-10 page-enter">
        <div className="text-3xl mb-4">📭</div>
        <h2 className="text-2xl font-black text-gray-800">Lesson not found</h2>
        <Link to="/dashboard" className="text-gold-700 mt-4 inline-block font-bold link-animate">Go back home</Link>
      </div>
    );
  }

  function selectAnswer(qIndex, option) {
    if (submitted) return;
    setAnswers({ ...answers, [qIndex]: option });
  }

  function handleSubmit() {
    if (Object.keys(answers).length < questions.length) return;
    setSubmitted(true);
    const finalScore = questions.filter((q, i) => answers[i] === q.answer).length;
    progressAPI.saveTestScore(Number(day), finalScore, questions.length).catch(() => {});
    if (finalScore === questions.length) {
      earnXP(50, "perfect_test"); playLevelUp();
      window.dispatchEvent(new CustomEvent("level:up"));
    } else if (finalScore >= questions.length / 2) {
      earnXP(25, "test"); playCorrect();
      window.dispatchEvent(new CustomEvent("lesson:complete"));
    } else {
      earnXP(10, "test"); playWrong();
      window.dispatchEvent(new CustomEvent("answer:wrong"));
    }
    if (finalScore >= questions.length / 2) {
      progressAPI.completeLesson(Number(day)).then((data) => {
        if (data.currentDay) localStorage.setItem("currentDay", String(data.currentDay));
      }).catch(() => {});
    }
  }

  function reset() { setAnswers({}); setSubmitted(false); setSeed((s) => s + 1); }
  function shuffleNew() { setAnswers({}); setSubmitted(false); setSeed((s) => s + 1); }

  const score = submitted ? questions.filter((q, i) => answers[i] === q.answer).length : 0;
  const isPerfect = score === questions.length;
  const isPassed = score >= questions.length / 2;

  return (
    <div className="space-y-3 page-enter relative">
      {/* Decorative blobs */}
      <div className="blob blob-emerald" style={{ width: 320, height: 320, top: -40, left: -40, opacity: 0.18 }} />
      <div className="blob blob-cyan" style={{ width: 260, height: 260, top: 200, right: -40, opacity: 0.15 }} />

      {/* Header */}
      <div className="relative">
        <Link to="/dashboard" className="text-gold-700 hover:text-gold-700 text-sm font-bold link-animate">&larr; Back to Dashboard</Link>
        <div className="flex items-center gap-3 mt-4 flex-wrap">
          <span className="chip-premium" style={{ background: "linear-gradient(135deg, rgba(16, 185, 129, 0.18), rgba(20, 184, 166, 0.18))", color: "#047857", borderColor: "rgba(16, 185, 129, 0.35)" }}>
            📝 Test
          </span>
          <h1 className="font-display text-3xl md:text-4xl font-normal text-ink-900">Day {lesson.day}: <em className="text-gold-600">{lesson.topic}</em></h1>
          {lesson.generatorKey && !submitted && (
            <button onClick={shuffleNew} className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gold-100 hover:bg-gold-200 text-gold-700 text-xs font-bold transition" title="Generate fresh questions">
              <Shuffle className="w-3.5 h-3.5" /> New Qs
            </button>
          )}
        </div>
        {/* Progress bar */}
        <div className="flex gap-1.5 mt-4">
          {questions.map((_, i) => (
            <div key={i} className={`flex-1 h-2 rounded-full transition-all duration-500 ${
              answers[i] !== undefined
                ? submitted
                  ? answers[i] === questions[i].answer ? "bg-sage-light" : "bg-berry-light"
                  : "bg-ink-100"
                : "bg-gray-200"
            }`} />
          ))}
        </div>
      </div>

      {/* Questions */}
      {questions.map((q, qIndex) => {
        const userAnswer = answers[qIndex];
        const isCorrect = userAnswer === q.answer;

        return (
          <div key={qIndex} className={`bg-white rounded-2xl p-6 sm:p-4 border transition-all duration-300 ${
            submitted && isCorrect ? "border-sage-light shadow-lg shadow-sage-light" :
            submitted && userAnswer && !isCorrect ? "border-berry-light shadow-lg shadow-berry-light" :
            "border-gray-100 card-hover"
          } fade-in stagger-${Math.min(qIndex + 1, 6)}`}>
            <div className="flex items-center justify-between mb-4">
              <p className="text-xs text-gray-400 font-bold uppercase tracking-wider">Question {qIndex + 1} of {questions.length}</p>
              {submitted && (
                <div className={`w-8 h-8 rounded-full flex items-center justify-center bounce-in ${isCorrect ? "bg-sage-light" : "bg-berry-light"}`}>
                  {isCorrect ? <Check className="w-4 h-4 text-sage-dark" /> : <X className="w-4 h-4 text-berry" />}
                </div>
              )}
            </div>
            <h2 className="text-lg font-black text-gray-900 mb-5">{q.question}</h2>
            <div className="grid gap-2.5">
              {q.options.map((option, oi) => {
                let style = "border-2 border-gray-100 bg-gray-50 text-gray-700 hover:border-gold-500 hover:bg-paper-deep hover:shadow-sm";
                if (userAnswer === option && !submitted) style = "border-2 border-gold-500 bg-paper-deep text-gold-700 shadow-md glow-border";
                if (submitted) {
                  if (option === q.answer) style = "border-2 border-sage bg-sage-light text-sage-dark shadow-sm";
                  else if (userAnswer === option && !isCorrect) style = "border-2 border-berry-light bg-berry-light text-berry shake";
                  else style = "border-2 border-gray-100 bg-gray-50 text-gray-300";
                }
                return (
                  <button key={option} onClick={() => selectAnswer(qIndex, option)}
                    className={`p-4 rounded-xl font-semibold text-left transition-all duration-200 ${style} ${submitted ? "cursor-default" : "cursor-pointer hover-lift"}`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-7 h-7 rounded-full border-2 flex items-center justify-center text-xs font-black flex-shrink-0 ${
                        submitted && option === q.answer ? "border-sage bg-sage-light text-white" :
                        submitted && userAnswer === option && !isCorrect ? "border-berry-light bg-berry-light text-white" :
                        userAnswer === option ? "border-gold-500 bg-ink-900 text-white" :
                        "border-gray-200 text-gray-400"
                      }`}>
                        {String.fromCharCode(65 + oi)}
                      </div>
                      {option}
                    </div>
                  </button>
                );
              })}
            </div>

            {submitted && (
              <div className={`mt-4 p-4 rounded-xl slide-in-left ${isCorrect ? "bg-sage-light border border-sage-light" : "bg-berry-light border border-berry-light"}`}>
                <p className={`font-bold text-sm ${isCorrect ? "text-sage-dark" : "text-berry"}`}>
                  {isCorrect ? "✅ Correct!" : `❌ Answer: ${q.answer}`}
                </p>
                <p className="text-gray-600 mt-1 text-sm">{q.explanation}</p>
              </div>
            )}
          </div>
        );
      })}

      {/* Submit / Score */}
      {!submitted ? (
        <button onClick={handleSubmit} disabled={Object.keys(answers).length < questions.length}
          className="btn-premium btn-premium-emerald px-8 py-3.5 disabled:opacity-40">
          <Zap className="w-5 h-5" /> Submit Answers
        </button>
      ) : (
        <div className={`rounded-2xl p-4 text-center bounce-in ${
          isPerfect ? "bg-gradient-to-br from-gold-100 to-gold-100 border-2 border-gold-300" :
          isPassed ? "bg-gradient-to-br from-sage-light to-sage-light border-2 border-sage-light" :
          "bg-gradient-to-br from-berry-light to-gold-100 border-2 border-berry-light"
        }`}>
          <div className="text-3xl mb-3">{isPerfect ? "🏆" : isPassed ? "👍" : "💪"}</div>
          <h2 className="text-2xl font-black text-gray-900 mb-1">{score} / {questions.length}</h2>
          <p className="text-gray-500 mb-2">
            {isPerfect ? "Perfect Score! You've mastered this!" : isPassed ? "Good job! Keep improving!" : "Don't give up! Review and try again."}
          </p>
          <p className="text-gold-700 font-bold text-sm mb-3">
            +{isPerfect ? 50 : isPassed ? 25 : 10} XP earned
          </p>
          <div className="flex gap-3 justify-center flex-wrap">
            <button onClick={reset} className="btn-premium btn-premium-primary px-6 py-3">
              <RotateCcw className="w-4 h-4" /> Retry
            </button>
            <Link to={`/lesson/${lesson.day}`} className="btn-premium bg-white text-gray-700 border-2 border-gray-200 hover:border-gold-500 px-6 py-3">
              <BookOpen className="w-4 h-4" /> Review
            </Link>
            {isPassed && Number(day) < lessons.length && (
              <Link to={`/lesson/${Number(day) + 1}`} className="btn-premium btn-premium-emerald px-6 py-3">
                Next <ArrowRight className="w-4 h-4" />
              </Link>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
